import React from "react";
import {
  Box,
  Chip,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Button,
  Card,
  CardContent,
  Fade,
} from "@mui/material";
import { Inbox, Mail, RefreshCcw } from "lucide-react";
import ReactPaginate from "react-paginate";
import { ChevronLeft, ChevronRight } from "lucide-react";
import LoadingPage from "../../Components/Common/Loading/LoadingPage";

export default function SidebarMailbox({
  loading,
  loadingRecipients = false,
  filter,
  setFilter,
  hideInbox = false,
  menuScopeFilter = "all",
  setMenuScopeFilter = () => {},
  messages,
  pagination,
  openMessage,
  setSelected,
  onPageChange = () => {},
  onRefreshMessages = () => {},
}) {
  const getFullName = (relation) => {
    const name = relation?.name || "";
    const surname = relation?.surname || "";
    return `${name} ${surname}`.trim();
  };

  const getSecondaryText = (msg) => {
    if (filter === "sent") {
      const toName = getFullName(msg?.to_relation);

      if (hideInbox && !toName) {
        return "Mensaje del CRM";
      }

      return `Para: ${toName || "CRM"}`;
    }

    const fromName = getFullName(msg?.from_relation);
    return `De: ${fromName || "Enviado desde el CRM"}`;
  };

  const hasValidCaseId = (value) => {
    if (value === null || value === undefined || value === "") return false;
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric > 0;
  };

  const handlePageClick = (event) => {
    const newPage = event.selected + 1;
    onPageChange(newPage);
  };

  return (
    <Box sx={{ width: "100%", height: "100%", minHeight: 0 }}>
      <Card
        // elevation={0}
        sx={{
          height: "100%",
          overflow: "hidden",
          mb: 0,
          borderRadius: "14px",
          border: "1px solid rgba(36, 72, 126, 0.16)",
          background:
            "linear-gradient(160deg, rgba(255,255,255,0.8) 0%, rgba(241,247,255,0.72) 100%)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 18px 38px -32px rgba(10, 38, 74, 0.78)",
        }}>
        <CardContent
          sx={{
            p: 0,
            height: "100%",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}>
          <Box sx={{ px: "10px", pt: "10px" }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Inbox />
              <Typography variant="h6">
                {hideInbox ? "Mensajes del expediente" : "Buzón"}
              </Typography>
              <Button
                size="small"
                variant="text"
                color="info"
                startIcon={<RefreshCcw size={16} />}
                sx={{ ml: "auto" }}
                onClick={onRefreshMessages}>
                Refrescar
              </Button>
            </Box>
          </Box>

          <Box sx={{ px: "10px" }}>
            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              startIcon={<Mail />}
              sx={{ mb: 2 }}
              onClick={() => setSelected("compose")}>
              Nuevo mensaje
            </Button>
          </Box>

          {loadingRecipients && (
            <Box sx={{ px: "10px" }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 1, display: "block" }}>
                Actualizando destinatarios por empresa...
              </Typography>
            </Box>
          )}

          <Box sx={{ px: "10px" }}>
            <Divider sx={{ mb: 2 }} />
          </Box>

          {!hideInbox && (
            <Box sx={{ px: "10px" }}>
              <Button
                fullWidth
                variant={filter === "inbox" ? "contained" : "outlined"}
                color="info"
                sx={{ mb: 1 }}
                onClick={() => setFilter("inbox")}>
                Bandeja de entrada
              </Button>
            </Box>
          )}

          <Box sx={{ px: "10px" }}>
            <Button
              fullWidth
              variant={filter === "sent" ? "contained" : "outlined"}
              color="success"
              sx={{ mb: 2 }}
              onClick={() => setFilter("sent")}>
              Enviados
            </Button>
          </Box>

          {!hideInbox && (
            <Box sx={{ px: "10px" }}>
              <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
                <Chip
                  label="Todos"
                  size="small"
                  clickable
                  color={menuScopeFilter === "all" ? "primary" : "default"}
                  onClick={() => setMenuScopeFilter("all")}
                />
                <Chip
                  label="Propios"
                  size="small"
                  clickable
                  color={menuScopeFilter === "normal" ? "primary" : "default"}
                  onClick={() => setMenuScopeFilter("normal")}
                />
                <Chip
                  label="Expedientes"
                  size="small"
                  clickable
                  color={menuScopeFilter === "case" ? "primary" : "default"}
                  onClick={() => setMenuScopeFilter("case")}
                />
              </Box>
            </Box>
          )}

          <Box sx={{ px: "10px" }}>
            <Divider sx={{ mb: 1 }} />
          </Box>

          <Box
            flex={1}
            overflow="auto"
            sx={{ minHeight: 0, pr: 0.5, px: "10px" }}>
            {loading ? (
              <LoadingPage isLoading heightProp="40vh" />
            ) : (
              <Fade in={!loading} timeout={800}>
                <List disablePadding>
                  {messages.length === 0 && (
                    <Box p={2}>
                      <Typography variant="body2" color="text.secondary">
                        No hay mensajes en esta vista.
                      </Typography>
                    </Box>
                  )}

                  {messages.map((msg) => (
                    <React.Fragment key={msg?.id}>
                      <ListItem disablePadding sx={{ mb: 1 }}>
                        <ListItemButton
                          onClick={() => openMessage(msg)}
                          sx={{
                            backgroundColor:
                              msg?.status === "unread" && filter !== "sent"
                                ? "rgba(25,118,210,.12)"
                                : "transparent",
                          }}>
                          <Card
                            variant="outlined"
                            sx={{
                              width: "100%",
                              overflow: "hidden",
                              borderRadius: "12px",
                            }}>
                            <CardContent sx={{ p: "10px !important" }}>
                              {!hideInbox && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    alignItems: "center",
                                    mb: 0.5,
                                  }}>
                                  <Chip
                                    size="small"
                                    label={
                                      msg?.scope_chip_label || "Correo normal"
                                    }
                                    sx={{
                                      backgroundColor: hasValidCaseId(
                                        msg?.case_id,
                                      )
                                        ? "#d8ebff"
                                        : "#ececec",
                                      color: "#27446b",
                                      fontWeight: 600,
                                      borderRadius: "8px",
                                    }}
                                  />
                                </Box>
                              )}
                              <ListItemText
                                primary={`ASUNTO: ${(msg?.subject || "").toUpperCase()}`}
                                secondary={getSecondaryText(msg)}
                              />
                            </CardContent>
                          </Card>
                        </ListItemButton>
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  ))}
                </List>
              </Fade>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              p: 1,
              mt: 1,
              borderTop: "1px solid rgba(36, 72, 126, 0.12)",

              /* Contenedor de la paginación */
              "& .pagination": {
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                listStyle: "none",
                gap: "6px",
                padding: 0,
                margin: 0,
                width: "100%",
                maxWidth: "100%",
              },

              /* Botones de página */
              "& .pagination li": {
                cursor: "pointer",
                borderRadius: "9px",
                border: "1px solid rgba(32, 79, 142, 0.22)",
                backgroundColor: "rgba(255,255,255,0.95)",
                color: "#284a74",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: { xs: "30px", sm: "34px" },
                height: { xs: "30px", sm: "34px" },
                transition: "all 0.2s ease",
                boxShadow: "0 8px 18px -16px rgba(18, 36, 64, 0.8)",
              },

              /* Hacer todo el li clickeable */
              "& .pagination li a, & .pagination li span": {
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textDecoration: "none",
                color: "inherit",
                padding: "0 6px",
              },

              /* Hover de botón */
              "& .pagination li:hover": {
                backgroundColor: "#f1f7ff",
                color: "#184b87",
                transform: "translateY(-1px)",
              },

              /* Página activa */
              "& .pagination li.active": {
                background: "linear-gradient(135deg, #1f5ea8 0%, #2b78d0 100%)",
                color: "#ffffff",
                borderColor: "#1f5ea8",
                boxShadow: "0 10px 20px -14px rgba(31, 94, 168, 0.9)",
                cursor: "default",
                transform: "none",
              },

              "& .pagination li.active:hover": {
                background: "linear-gradient(135deg, #1f5ea8 0%, #2b78d0 100%)",
                color: "#ffffff",
                transform: "none",
              },

              /* Deshabilitado (prev/next si los hay) */
              "& .pagination li.disabled": {
                opacity: 0.38,
                pointerEvents: "none",
                cursor: "not-allowed",
              },
            }}>
            <ReactPaginate
              nextLabel={<ChevronRight size={16} />}
              previousLabel={<ChevronLeft size={16} />}
              pageCount={pagination.last_page || 1}
              containerClassName="pagination"
              activeClassName="active"
              disabledClassName="disabled"
              pageRangeDisplayed={2}
              marginPagesDisplayed={1}
              breakLabel="..."
              forcePage={(pagination.page || 1) - 1}
              onPageChange={handlePageClick}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
